/**
 * Keyboard sounds, synthesised with WebAudio — no audio files to download.
 *
 * Every pack answers four kinds of stroke: a normal key, Space, Backspace and a
 * wrong key, plus a chime when a run finishes. Each hit is varied a little in
 * pitch and loudness so a fast run sounds like a real keyboard instead of a
 * machine gun of identical samples.
 *
 * The AudioContext is created lazily on the first sound, which always follows a
 * key press or click, so browsers' autoplay rules never block it.
 */

export const SOUND_PACKS = ['mechanical', 'typewriter', 'laptop', 'bubble', 'piano'];

let ctx = null;
let master = null;
let noise = null;

function audio() {
    if (typeof window === 'undefined') return null;
    try {
        if (!ctx) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return null;
            ctx = new Ctx({ latencyHint: 'interactive' });
            master = ctx.createGain();
            // A gentle limiter so overlapping strokes never clip.
            const limiter = ctx.createDynamicsCompressor();
            limiter.threshold.value = -10;
            limiter.ratio.value = 8;
            master.connect(limiter);
            limiter.connect(ctx.destination);
            const len = Math.floor(ctx.sampleRate * 0.5);
            noise = ctx.createBuffer(1, len, ctx.sampleRate);
            const data = noise.getChannelData(0);
            for (let i = 0; i < len; i += 1) data[i] = Math.random() * 2 - 1;
        }
        if (ctx.state === 'suspended') ctx.resume();
        return ctx;
    } catch {
        return null;
    }
}

/** 0–100 slider → output gain, on a curve so the low end stays usable. */
const volumeGain = (volume) => Math.pow(Math.max(0, Math.min(100, volume)) / 100, 1.6) * 0.9;
const jitter = (amount) => 1 + (Math.random() * 2 - 1) * amount;

/** A decaying envelope on a fresh gain node: quick attack, exponential tail. */
function envelope(c, t, peak, decay, attack = 0.002) {
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    return g;
}

/** Filtered noise burst — the "click" of a switch or a typebar hitting paper. */
function burst(c, out, t, { type = 'bandpass', freq = 3000, q = 1, peak = 0.5, decay = 0.02 }) {
    const src = c.createBufferSource();
    src.buffer = noise;
    src.playbackRate.value = jitter(0.15);
    const f = c.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq * jitter(0.08);
    f.Q.value = q;
    const g = envelope(c, t, peak, decay);
    src.connect(f); f.connect(g); g.connect(out);
    src.start(t, Math.random() * 0.3);
    src.stop(t + decay + 0.05);
}

/** A pitched body — sine/triangle that can sweep, for thocks, bubbles and notes. */
function tone(c, out, t, { type = 'sine', from = 200, to = from, sweep = 0.04, peak = 0.4, decay = 0.06, attack = 0.002 }) {
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(from, t);
    if (to !== from) o.frequency.exponentialRampToValueAtTime(Math.max(to, 20), t + sweep);
    const g = envelope(c, t, peak, decay, attack);
    o.connect(g); g.connect(out);
    o.start(t);
    o.stop(t + attack + decay + 0.05);
}

// Pentatonic notes for the piano pack: each physical key keeps its own note, so a
// familiar word plays the same little tune every time.
const PENTA = [261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33, 659.25, 783.99, 880.0];
const noteFor = (code = '') => {
    let h = 0;
    for (let i = 0; i < code.length; i += 1) h = (h * 31 + code.charCodeAt(i)) >>> 0;
    return PENTA[h % PENTA.length];
};

const PACKS = {
    // Clicky mechanical switch: a sharp click on top of a short low "thock".
    mechanical(c, out, t, kind) {
        if (kind === 'space') {
            burst(c, out, t, { type: 'lowpass', freq: 1400, q: 0.7, peak: 0.55, decay: 0.05 });
            tone(c, out, t, { from: 130 * jitter(0.05), to: 70, sweep: 0.06, peak: 0.55, decay: 0.09 });
            return;
        }
        const p = jitter(0.06);
        burst(c, out, t, { freq: (kind === 'back' ? 3400 : 4300) * p, q: 2.2, peak: 0.55, decay: 0.014 });
        burst(c, out, t + 0.012, { freq: 2600 * p, q: 1.2, peak: 0.18, decay: 0.02 });
        tone(c, out, t, { from: 210 * p, to: 95, sweep: 0.035, peak: 0.42, decay: 0.05 });
    },
    // Manual typewriter: a hard typebar strike with a metallic ring, a heavy carriage on Space.
    typewriter(c, out, t, kind) {
        if (kind === 'space') {
            burst(c, out, t, { type: 'lowpass', freq: 900, q: 0.8, peak: 0.6, decay: 0.08 });
            tone(c, out, t, { type: 'triangle', from: 95, to: 55, sweep: 0.08, peak: 0.5, decay: 0.12 });
            burst(c, out, t + 0.03, { freq: 5200, q: 6, peak: 0.08, decay: 0.05 });
            return;
        }
        const p = jitter(0.07);
        burst(c, out, t, { freq: 2300 * p, q: 1.6, peak: 0.75, decay: 0.03 });
        tone(c, out, t, { type: 'square', from: 1900 * p, peak: 0.05, decay: 0.012 });
        tone(c, out, t, { type: 'triangle', from: 120 * p, to: 70, sweep: 0.03, peak: 0.45, decay: 0.05 });
    },
    // Quiet laptop / membrane keys: a muffled tap, nothing sharp.
    laptop(c, out, t, kind) {
        const p = jitter(0.06);
        burst(c, out, t, { type: 'lowpass', freq: (kind === 'space' ? 900 : 1700) * p, q: 0.6, peak: kind === 'space' ? 0.5 : 0.42, decay: kind === 'space' ? 0.045 : 0.028 });
        tone(c, out, t, { from: (kind === 'space' ? 150 : 260) * p, to: 120, sweep: 0.02, peak: 0.16, decay: 0.03 });
    },
    // Soft bubble pops that rise in pitch.
    bubble(c, out, t, kind) {
        const base = kind === 'space' ? 260 : kind === 'back' ? 360 : 480;
        const p = jitter(0.12);
        tone(c, out, t, { from: base * p, to: base * 2.1 * p, sweep: 0.03, peak: 0.42, decay: 0.07, attack: 0.004 });
    },
    // Each key plays a soft note from a pentatonic scale — never out of tune.
    piano(c, out, t, kind, code) {
        const f = kind === 'space' ? 196 : kind === 'back' ? 220 : noteFor(code);
        tone(c, out, t, { type: 'triangle', from: f, peak: 0.38, decay: 0.38, attack: 0.004 });
        tone(c, out, t, { from: f * 2, peak: 0.08, decay: 0.2, attack: 0.004 });
    },
};

/** One keystroke. `kind` is 'key' | 'space' | 'back'. */
export function playKey(pack, { kind = 'key', code = '', volume = 60 } = {}) {
    const c = audio();
    const play = PACKS[pack];
    if (!c || !play || volume <= 0) return;
    const out = c.createGain();
    out.gain.value = volumeGain(volume) * jitter(0.08);
    out.connect(master);
    play(c, out, c.currentTime + 0.001, kind, code);
}

/**
 * A wrong key: a short, dry "bonk" — a falling low tone with a muted thump and a
 * slightly dissonant overtone. Different from every pack's click, so it reads as
 * "wrong" at once, and short enough to repeat on every wrong press without blurring.
 */
export function playError(volume = 60) {
    const c = audio();
    if (!c || volume <= 0) return;
    const out = c.createGain();
    out.gain.value = volumeGain(Math.max(volume, 25)) * 0.9; // stays audible at low volumes
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1400;
    out.connect(lp); lp.connect(master);
    const t = c.currentTime + 0.001;
    burst(c, out, t, { type: 'lowpass', freq: 500, q: 0.8, peak: 0.45, decay: 0.03 });
    tone(c, out, t, { type: 'triangle', from: 330, to: 150, sweep: 0.09, peak: 0.55, decay: 0.11 });
    tone(c, out, t, { type: 'square', from: 233, to: 110, sweep: 0.09, peak: 0.12, decay: 0.09 });
}

/** Start (or resume) the audio engine ahead of the first key, so it plays on time. */
export function warmAudio() {
    audio();
}

/** End of a run: a rising chime on success, a gentle two-note fall otherwise. */
export function playFinish(pack, { volume = 60, success = true } = {}) {
    const c = audio();
    if (!c || volume <= 0) return;
    const out = c.createGain();
    out.gain.value = volumeGain(volume) * 0.8;
    out.connect(master);
    const t = c.currentTime + 0.02;
    if (pack === 'typewriter') {
        // The carriage-return bell.
        tone(c, out, t, { from: 2093, peak: 0.35, decay: 1.1, attack: 0.002 });
        tone(c, out, t, { from: 4186, peak: 0.08, decay: 0.6, attack: 0.002 });
        return;
    }
    const notes = success ? [523.25, 659.25, 783.99, 1046.5] : [440, 349.23];
    notes.forEach((f, i) => {
        tone(c, out, t + i * 0.09, { type: 'triangle', from: f, peak: 0.32, decay: success ? 0.5 : 0.4, attack: 0.006 });
        tone(c, out, t + i * 0.09, { from: f * 2, peak: 0.06, decay: 0.3, attack: 0.006 });
    });
}

/** A short phrase in the chosen pack, for the settings preview button. */
export function previewPack(pack, volume = 60) {
    const c = audio();
    if (!c) return;
    const seq = [['key', 'KeyF'], ['key', 'KeyJ'], ['key', 'KeyD'], ['space', 'Space'], ['key', 'KeyK'], ['key', 'KeyL']];
    seq.forEach(([kind, code], i) => {
        window.setTimeout(() => playKey(pack, { kind, code, volume }), i * 115 + Math.random() * 25);
    });
}
